import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';

const SCROLL_THRESHOLD = 300;

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  // Track previous visibility to avoid redundant state updates on every scroll event
  const prevVisibleRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const shouldBeVisible = window.scrollY > SCROLL_THRESHOLD;
      // Only update state when visibility actually changes to minimize re-renders
      if (shouldBeVisible !== prevVisibleRef.current) {
        prevVisibleRef.current = shouldBeVisible;
        setIsVisible(shouldBeVisible);
      }
    };
    // passive: true tells the browser this handler won't call preventDefault(), enabling scroll optimizations
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);

  // Hide the button until the user has scrolled past the threshold
  if (!isVisible) return null;

  return (
    <Button aria-label='Scroll to top' css={buttonStyle} onClick={handleClick} variant='primary' iconName='caret-up' />
  );
};

export default ScrollToTop;

const buttonStyle = css`
  position: fixed;
  bottom: ${theme.spacing(4)};
  right: ${theme.spacing(4)};
  z-index: 200;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  box-shadow: ${theme.shadows.heavy};
  transition: transform 150ms ease;
  background-color: ${theme.colors.text.default};

  &:hover:not([disabled]) {
    transform: translateY(-2px);
    background-color: ${theme.colors.text.default};
  }

  &:active:not([disabled]) {
    transform: translateY(0);
    background-color: ${theme.colors.text.default};
  }
`;
